/**
 * @fileoverview NotificationBell (admin)
 *
 * Renderiza el bell del header. Consume `NotificationsContext` —
 * todo el state (items, unread, SSE, toast) vive ahí. Este componente
 * solo se encarga del UI.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Spinner } from '@/components/ui';
import { useNotifications } from '@/contexts/NotificationsContext';
import type { InAppNotification } from '@/services/notification.service';

const MAX_PREVIEW = 10;

function timeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'ahora';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return new Date(iso).toLocaleDateString('es-CO', { day: '2-digit', month: 'short' });
}

export function NotificationBell() {
  const navigate = useNavigate();
  const { items, unread, isLoading, markAllRead, openNotification } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; right: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Solo mostramos las primeras N en el dropdown — el resto cae a la
  // página completa de inbox.
  const previewItems = items.slice(0, MAX_PREVIEW);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen]);

  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setPosition({
      top: rect.bottom + 8,
      right: window.innerWidth - rect.right,
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const onScroll = (e: Event) => {
      const target = e.target as HTMLElement;
      if (dropdownRef.current && dropdownRef.current.contains(target)) {
        return;
      }
      setIsOpen(false);
    };
    const onResize = () => updatePosition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [isOpen, updatePosition]);

  const handleToggle = () => {
    if (!isOpen) {
      updatePosition();
    }
    setIsOpen((prev) => !prev);
  };

  const handleClick = (n: InAppNotification) => {
    setIsOpen(false);
    openNotification(n);
  };

  return (
    <div className="relative">
      <motion.button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        whileTap={{ scale: 0.92 }}
        className="relative inline-flex items-center justify-center h-10 w-10 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={`Notificaciones${unread > 0 ? ` (${unread} sin leer)` : ''}`}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-error text-error-foreground text-[10px] font-bold leading-none">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </motion.button>

      {createPortal(
        <AnimatePresence>
          {isOpen && position && (
            <motion.div
              ref={dropdownRef}
              initial={{ opacity: 0, scale: 0.96, y: -4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98, y: -2, transition: { duration: 0.12 } }}
              className="fixed w-[360px] z-[9999] bg-background border border-border rounded-lg overflow-hidden"
              style={{
                top: position.top,
                right: position.right,
                transformOrigin: 'top right',
                boxShadow: 'var(--shadow-elevated, 0 10px 30px rgba(0,0,0,0.12))',
              }}
              role="menu"
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-border-secondary">
                <h3 className="text-sm font-semibold">Notificaciones</h3>
                {unread > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      void markAllRead();
                    }}
                    className="text-xs text-primary hover:underline"
                  >
                    Marcar todas leídas
                  </button>
                )}
              </div>
              <div className="max-h-96 overflow-y-auto">
                {isLoading && previewItems.length === 0 ? (
                  <div className="p-6 text-center">
                    <Spinner size="sm" className="mx-auto" />
                  </div>
                ) : previewItems.length === 0 ? (
                  <div className="p-6 text-center text-sm text-muted-foreground">
                    Sin notificaciones todavía.
                  </div>
                ) : (
                  <ul className="divide-y divide-border-secondary">
                    {previewItems.map((n) => {
                      const isUnread = !n.readAt;
                      return (
                        <li key={n.id}>
                          <button
                            type="button"
                            onClick={() => handleClick(n)}
                            className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-muted/50 transition-colors ${
                              isUnread ? 'bg-primary/5' : ''
                            }`}
                            role="menuitem"
                          >
                            <span
                              className={`mt-1.5 inline-block w-2 h-2 rounded-full shrink-0 ${
                                isUnread ? 'bg-primary' : 'bg-transparent'
                              }`}
                              aria-hidden="true"
                            />
                            <div className="min-w-0 flex-1">
                              <p
                                className={`text-sm ${isUnread ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}
                              >
                                {n.title}
                              </p>
                              <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{n.body}</p>
                              <p className="text-[10px] text-muted-foreground mt-1 tabular-nums">
                                {timeAgo(n.createdAt)}
                              </p>
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <div className="border-t border-border-secondary p-2">
                <button
                  type="button"
                  onClick={() => {
                    setIsOpen(false);
                    navigate('/notifications');
                  }}
                  className="w-full text-center text-sm py-2 rounded-md hover:bg-muted text-primary transition-colors"
                >
                  Ver todas
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </div>
  );
}
